import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { fetchAdmin, toErrorMessage } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import TransactionSideDrawer from '../TransactionSideDrawer';
import { AdminErrorState, AdminScreenLayout, AdminStatusChip } from '../shared';
import { removeTrailingZeros } from 'utils/formatNumber';
import ProfileDialog from 'sections/@dashboard/user/ProfileDialog';
import Iconify from 'components/Iconify';

const STATUS_OPTIONS = ['pending', 'completed', 'failed'];

const TRANSACTION_TYPE_OPTIONS = [
  'blockchain.withdrawal',
  'bank.transfer',
  'wallet.bank.deposit',
  'blockchain.deposit',
  'wallet.deposit',
  'giftcard.purchase',
  'blockchain',
];

const DEFAULT_FILTERS = {
  user_id: '',
  reference: '',
  status: '',
  transaction_type: '',
  start_date: null,
  end_date: null,
  page: 0,
  page_size: 50,
};

function formatAmount(row) {
  const currency = row.display_currency || row.currency || '';
  const amount = row.display_amount ?? row.amount;

  if (amount === undefined || amount === null || amount === '') {
    return 'N/A';
  }

  return [currency, amount].filter(Boolean).join(' ').trim();
}

function formatDateInput(date) {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function buildQueryFilters(filters) {
  return {
    page: filters.page + 1,
    page_size: filters.page_size,
    user_id: filters.user_id || undefined,
    reference: filters.reference || undefined,
    status: filters.status || undefined,
    transaction_type: filters.transaction_type || undefined,
    start_date: filters.start_date ? new Date(filters.start_date).toISOString() : undefined,
    end_date: filters.end_date ? new Date(filters.end_date).toISOString() : undefined,
  };
}

function FilterChipRow({ filters, onClearOne, onClearAll }) {
  const active = [];

  if (filters.reference) active.push({ key: 'reference', label: `Ref: ${filters.reference}` });
  if (filters.user_id) active.push({ key: 'user_id', label: `User: ${filters.user_id}` });
  if (filters.status) active.push({ key: 'status', label: `Status: ${filters.status}` });
  if (filters.transaction_type) active.push({ key: 'transaction_type', label: `Type: ${filters.transaction_type}` });
  if (filters.start_date) active.push({ key: 'start_date', label: `From: ${new Date(filters.start_date).toLocaleDateString()}` });
  if (filters.end_date) active.push({ key: 'end_date', label: `To: ${new Date(filters.end_date).toLocaleDateString()}` });

  if (!active.length) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      {active.map((item) => (
        <Chip
          key={item.key}
          label={item.label}
          onDelete={() => onClearOne(item.key)}
          variant="outlined"
          sx={{ mb: 1 }}
        />
      ))}
      <Button size="small" onClick={onClearAll}>
        Clear all
      </Button>
    </Stack>
  );
}

FilterChipRow.propTypes = {
  filters: PropTypes.object.isRequired,
  onClearOne: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
};

export default function TransactionsView({ breadcrumbs, title }) {
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [state, setState] = useState({
    loading: false,
    loaded: false,
    error: null,
    data: [],
    total: 0,
  });
  const [selectedReference, setSelectedReference] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileDialog,setProfileDialog] = useState({open:false,user:"",});

  useEffect(() => {
    let active = true;

    const load = async () => {
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const response = await fetchAdmin('/transactions/history', buildQueryFilters(filters));

        if (!active) return;

        setState({
          loading: false,
          loaded: true,
          error: null,
          data: response?.data || [],
          total: Number(response?.total_size || 0),
        });
      } catch (error) {
        if (!active) return;

        setState((prev) => ({
          ...prev,
          loading: false,
          loaded: true,
          error: toErrorMessage(error),
        }));
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [filters]);

  const rows = useMemo(
    () =>
      state.data.map((row) => ({
        id: row.id,
        reference: row.reference || 'N/A',
        userId: row.user_id || 'N/A',
        transactionType: row.transaction_type || 'N/A',
        status: row.status || 'unknown',
        amount: formatAmount(row),
        first_name: row.first_name,
        last_name: row.last_name,
        createdAt: row.created_at ? fDateTime(row.created_at) : 'N/A',
      })),
    [state.data]
  );

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'User',
        width: 180,
        editable: false,
        renderCell: (params) => (
          <>
            <IconButton color="primary" onClick={()=> setProfileDialog({open:true,user:{user_id:params.row.userId}}) }>
              <Iconify icon={'ph:user-thin'} size="small" sx={{width:25,height:25}} />
            </IconButton>
            <Typography variant="subtitle2">
              {params.row.first_name} {params.row.last_name}
            </Typography>
          </>
        ),
        valueGetter: (params) => `${params.row.first_name} ${params.row.last_name}`
      },
      {
        field: 'reference',
        headerName: 'Reference',
        flex: 1.2,
        minWidth: 220,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }} noWrap>
            <IconButton color="primary" onClick={() => {
              setSelectedReference(params.row.reference);
              setDrawerOpen(true);
            }}>
              <Iconify icon={'ion:open-outline'} size="small" sx={{ width: 25, height: 25 }} />
            </IconButton>
            {params.value}
          </Typography>
        ),
      },
      
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 140,
        renderCell: (params) => <AdminStatusChip status={params.value} />,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 0.8,
        minWidth: 160,
        valueGetter: (params) => `${removeTrailingZeros(params.row.amount)}`,
        renderCell: (params) => (
          <Typography variant="body2">{String(params.value).toLocaleUpperCase()}</Typography>
        ),
      },
      {
        field: 'transactionType',
        headerName: 'Transaction Type',
        flex: 1,
        minWidth: 200,
      },
      { field: 'createdAt', headerName: 'Created', flex: 0.9, minWidth: 190 },
    ],
    []
  );

  const applyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      ...draftFilters,
      page: 0,
    }));
  };

  const clearOne = (key) => {
    setDraftFilters((prev) => ({ ...prev, [key]: DEFAULT_FILTERS[key] }));
    setFilters((prev) => ({ ...prev, [key]: DEFAULT_FILTERS[key], page: 0 }));
  };

  const clearAll = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  };

  return (
    <AdminScreenLayout title={title} breadcrumbs={breadcrumbs}>
      <Stack spacing={3}>
        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Transaction Filters</Typography>
                <Typography variant="body2" color="text.secondary">
                  Filter by reference, user, status, transaction type, and date range.
                </Typography>
              </Stack>

              <FilterChipRow filters={filters} onClearOne={clearOne} onClearAll={clearAll} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reference"
                    value={draftFilters.reference}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({ ...prev, reference: event.target.value }))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="User ID"
                    value={draftFilters.user_id}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({ ...prev, user_id: event.target.value }))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Status"
                    value={draftFilters.status}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({ ...prev, status: event.target.value }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Transaction Type"
                    value={draftFilters.transaction_type}
                    onChange={(event) =>
                      setDraftFilters((prev) => ({ ...prev, transaction_type: event.target.value }))
                    }
                  >
                    <MenuItem value="">All</MenuItem>
                    {TRANSACTION_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="From date"
                      value={formatDateInput(draftFilters.start_date)}
                      onChange={(newValue) =>
                        setDraftFilters((prev) => ({ ...prev, start_date: newValue }))
                      }
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="To date"
                      value={formatDateInput(draftFilters.end_date)}
                      onChange={(newValue) =>
                        setDraftFilters((prev) => ({ ...prev, end_date: newValue }))
                      }
                      renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                    />
                  </Grid>
                </LocalizationProvider>
              </Grid>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="end">
                
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" onClick={clearAll}>
                    Clear
                  </Button>
                  <LoadingButton variant="contained" loading={state.loading} onClick={applyFilters}>
                    Apply Filters
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderRadius: 2.5 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Transaction History</Typography>
                <Typography variant="body2" color="text.secondary">
                  Open the drawer to inspect ledger, payload, policy checks, and transaction lifecycle data.
                </Typography>
              </Stack>

              {state.error ? <AdminErrorState title="Transaction history failed" message={state.error} /> : null}

              <Box sx={{ height: 680, width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  loading={state.loading}
                  pagination
                  disableRowSelectionOnClick
                  paginationMode="server"
                  rowCount={state.total}
                  page={filters.page}
                  onPageChange={(nextPage) => {
                    setFilters((prev) => ({
                      ...prev,
                      page: nextPage,
                    }));
                    setDraftFilters((prev) => ({
                      ...prev,
                      page: nextPage,
                    }));
                  }}
                  pageSize={filters.page_size}
                  onPageSizeChange={(nextPageSize) => {
                    setFilters((prev) => ({
                      ...prev,
                      page: 0,
                      page_size: nextPageSize,
                    }));
                    setDraftFilters((prev) => ({
                      ...prev,
                      page: 0,
                      page_size: nextPageSize,
                    }));
                  }}
                  pageSizeOptions={[25, 50, 100]}
                  rowsPerPageOptions={[25, 50, 100]}
                  // onRowClick={(params) => {
                  //   setSelectedReference(params.row.reference);
                  //   setDrawerOpen(true);
                  // }}
                  getRowHeight={() => 64}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <TransactionSideDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          reference={selectedReference}
        />

        {profileDialog.open &&  <ProfileDialog
            user={profileDialog.user}
            refresh
            handleClose={()=>{
              setProfileDialog({open:false});
            }}
              />}
      </Stack>
    </AdminScreenLayout>
  );
}

TransactionsView.propTypes = {
  breadcrumbs: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string,
};

TransactionsView.defaultProps = {
  title: 'Transactions',
};
